import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Product, ProductDialogType} from '../../../shared/models/models';
import {FormBuilder, Validators} from "@angular/forms";
import {NGXLogger} from "ngx-logger";
import {environment} from "../../../../environments/environment";
import {MatTableDataSource} from "@angular/material/table";

export interface ProductDescriptor {
  id: number;
  descriptor: string;
  isNew: boolean;
}

@Component({
  selector: 'app-product-edit',
  templateUrl: './product-edit.component.html',
  styleUrls: ['./product-edit.component.scss']
})
export class ProductEditComponent implements OnInit {
  dialogTitle!: string;
  buttonText: string = "";
  product!: Product;
  descriptorList = new MatTableDataSource<ProductDescriptor>([]);
  displayedColumns: string[] = ['descriptor', 'edit', 'delete'];
  activateAddDescriptor: boolean = false;
  activateEditDescriptor: boolean = false;
  activeProduct: ProductDescriptor = {id: 0, descriptor: "", isNew: false};
  interestPlanCodesString: string = "";

  productEditForm = this.formBuilder.group({
    name: ['', [Validators.required]],
    uid: ['', [Validators.required]],
    code: ['', [Validators.required]],
    isActive: ['', [Validators.required]],
    descriptors: [''],
    infoUrl: [''],
    accountType: ['', [Validators.required]],
    productType: ['', [Validators.required]],
    alloyWorkflowId: [''],
    olbGroupId: ['', [Validators.required]],
    interestPlanCodes: [''],
    maxNumberOfAccounts: ['', [Validators.required]],
    allowJointApplications: ['', [Validators.required]],
    freeDebitCard: [''],
    freeStandardCheck: [''],
    allowMultiProductApplications: ['', [Validators.required]]
  });

  addDescriptorForm = this.formBuilder.group({
    descriptor: ['', [Validators.required]]
  });

  editDescriptorForm = this.formBuilder.group({
    descriptor: ['', [Validators.required]]
  });

  /**
   * Constructor
   * @param formBuilder Form builder instance
   * @param dialogRef Dialog reference
   * @param data Data passed into dialog
   * @param logger Logger instance
   */
  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<ProductEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { dialogType: ProductDialogType, product: Product, title: string },
    private logger: NGXLogger) {
  }

  /**
   * Init
   */
  ngOnInit(): void {
    this.product = this.data.product;

    this.dialogTitle = `${this.data.title}`;
    this.dialogTitle += this.data.dialogType === ProductDialogType.Edit ? ` ${this.data.product.code} - ${this.data.product.name}` : '';
    this.buttonText = this.data.dialogType === ProductDialogType.Edit ? "Save Edits" : "Add";

    if (this.product.descriptors !== "") {
      let descriptorSplit = this.product.descriptors.split("|");

      descriptorSplit.forEach((descriptor, index) => {
        this.descriptorList.data.push({id: index + 1, descriptor: descriptor, isNew: false})
      });
    }

    console.log(this.product.interestPlanCodes);

    this.interestPlanCodesString = this.product.interestPlanCodes.join(",");

    if (!environment.production) console.log(this.descriptorList.data);
  }

  /**
   * Called when ok button is clicked
   */
  onOk(): void {
    this.product.descriptors = this.createDescriptorString();
    this.product.interestPlanCodes = this.createInterestPlanCodes();

    this.dialogRef.close(this.product);
  }

  private createInterestPlanCodes(): number[] {
    let interestPlanCodes: number[] = [];

    //split only if length > 0, to avoid setting default array
    //Number("") always return 0
    //To prevent adding a default integer value of "0" to the interestPlanCodes array.
    if (this.interestPlanCodesString.length > 0) {
      this.interestPlanCodesString.split(",").forEach(interestPlan => {
        interestPlanCodes.push(Number(interestPlan.trim()));
      });
    }
    return interestPlanCodes;
  }

  private createDescriptorString(): string {
    let descriptorsString = '';
    const newData = [...this.descriptorList.data];

    newData.forEach(descriptor => {
      descriptorsString += `${descriptor.descriptor}|`;
    });

    this.descriptorList.data = newData;

    // remove trailing '|'
    return descriptorsString.slice(0, -1);
  }

  toggleAddDescriptor() {
    this.activateAddDescriptor = !this.activateAddDescriptor;
  }

  activateModifyDescriptor(id: number, descriptor: string) {
    this.activeProduct = {id, descriptor, isNew: false};
    this.activateEditDescriptor = true;
    this.editDescriptorForm.controls['descriptor'].setValue(descriptor);
  }

  onCancelAddDescriptor() {
    this.activateAddDescriptor = false;

    this.addDescriptorForm.reset();
  }

  onCancelModifyDescriptor() {
    this.activateEditDescriptor = false;

    this.editDescriptorForm.reset();
  }

  onModifyDescriptor() {
    const descriptorText = this.editDescriptorForm.get('descriptor')?.value;
    const newData = [...this.descriptorList.data];

    newData.forEach(descriptor => {
      // find the correct descriptor being modified
      if (descriptor.id === this.activeProduct.id) {
        // set the new text
        descriptor.descriptor = descriptorText;
      }
    });

    this.descriptorList.data = newData;

    // reset
    this.activeProduct = {id: 0, descriptor: "", isNew: false}
    this.activateEditDescriptor = false;
    this.editDescriptorForm.reset();
  }

  onSaveNewDescriptor() {
    const descriptorText = this.addDescriptorForm.get('descriptor')?.value;
    const newData = [...this.descriptorList.data];

    newData.push({id: this.descriptorList.data.length + 1, descriptor: descriptorText, isNew: true});

    this.descriptorList.data = newData;

    this.activateAddDescriptor = false;

    this.addDescriptorForm.reset();
  }

  onDeleteDescriptor(id: number) {
    this.descriptorList.data = this.descriptorList.data.filter(descriptor => descriptor.id !== id);
  }
}
